import { z } from 'zod';
import { cashFlowEntrySchema, type CashFlowEntry, type CashFlowRequestOptions } from '@/lib/cashFlow';

const entryResponseSchema = z.object({ entry: cashFlowEntrySchema });
const errorEnvelopeSchema = z.object({ error: z.object({ message: z.string() }) });

function authHeaders(accessToken?: string, extra: Record<string, string> = {}): Record<string, string> {
  return accessToken ? { ...extra, Authorization: `Bearer ${accessToken}` } : extra;
}

async function failureMessage(res: Response, fallback: string): Promise<string> {
  const body: unknown = await res.json().catch(() => null);
  const parsed = errorEnvelopeSchema.safeParse(body);
  return parsed.success ? parsed.data.error.message : fallback;
}

async function parseEntry(res: Response, fallback: string): Promise<CashFlowEntry> {
  if (!res.ok) throw new Error(await failureMessage(res, fallback));
  return entryResponseSchema.parse(await res.json()).entry;
}

export interface AddKmdPaymentInput {
  paidOn: string;
  amountCents: number;
  idempotencyKey: string;
  finalizeMonth?: boolean;
}

export async function addKmdManualPayment(
  entryId: string,
  input: AddKmdPaymentInput,
  options: CashFlowRequestOptions = {},
): Promise<CashFlowEntry> {
  const { fetchImpl = fetch, baseUrl = '', accessToken } = options;
  const res = await fetchImpl(`${baseUrl}/api/kmd/entries/${encodeURIComponent(entryId)}/payments`, {
    method: 'POST',
    headers: authHeaders(accessToken, { 'Content-Type': 'application/json' }),
    body: JSON.stringify(input),
  });
  return parseEntry(res, `KMD makse lisamine ebaõnnestus: ${res.status}`);
}

export async function deleteKmdManualPayment(
  paymentId: string,
  options: CashFlowRequestOptions = {},
): Promise<CashFlowEntry> {
  const { fetchImpl = fetch, baseUrl = '', accessToken } = options;
  const res = await fetchImpl(`${baseUrl}/api/kmd/payments/${encodeURIComponent(paymentId)}`, {
    method: 'DELETE',
    headers: authHeaders(accessToken),
  });
  return parseEntry(res, `KMD makse eemaldamine ebaõnnestus: ${res.status}`);
}

export interface UpdateKmdPaymentInput {
  paidOn: string;
  amountCents: number;
}

/** Correct one manual KMD payment while preserving the audited payment event. */
export async function updateKmdManualPayment(
  paymentId: string,
  input: UpdateKmdPaymentInput,
  options: CashFlowRequestOptions = {},
): Promise<CashFlowEntry> {
  const { fetchImpl = fetch, baseUrl = '', accessToken } = options;
  const res = await fetchImpl(`${baseUrl}/api/kmd/payments/${encodeURIComponent(paymentId)}`, {
    method: 'PATCH',
    headers: authHeaders(accessToken, { 'Content-Type': 'application/json' }),
    body: JSON.stringify(input),
  });
  return parseEntry(res, `KMD makse muutmine ebaõnnestus: ${res.status}`);
}

/**
 * Finalize (`finalized: true` → POST) or reopen (`finalized: false` → DELETE) a
 * declaration month. The API models the flag as a resource on ONE path,
 * `/kmd/entries/:id/finalize` — POST sets it, DELETE clears it (no body either
 * way). One truth per route (master rule): the client never invents a payload
 * the server does not read.
 */
export async function setKmdFinalized(
  entryId: string,
  finalized: boolean,
  options: CashFlowRequestOptions = {},
): Promise<CashFlowEntry> {
  const { fetchImpl = fetch, baseUrl = '', accessToken } = options;
  const res = await fetchImpl(`${baseUrl}/api/kmd/entries/${encodeURIComponent(entryId)}/finalize`, {
    method: finalized ? 'POST' : 'DELETE',
    headers: authHeaders(accessToken),
  });
  return parseEntry(
    res,
    finalized ? `KMD kuu sulgemine ebaõnnestus: ${res.status}` : `KMD kuu avamine ebaõnnestus: ${res.status}`,
  );
}